/**
 * LockoutHost — the daily lockouts (Time, Captain's report, Thread triage, Progress) on the tools
 * that open in their own window: AlderCAD, AlderQC, document markup, AlderSPEC, Sketch
 * (Matt, 2026-10-15: "if someone leaves a model open overnight and they return to work on it the
 * next day, they may not even return to the outline and experience the lockout").
 *
 * The outline mounts the same four gates itself (OutlineView.jsx). They need two things the outline
 * normally sets up: ctx.currentMember (the Airtable Member, resolved from the Clerk email) and the
 * seat (loadCurrentUserSeat). This host does both, then mounts the gates. Client-facing and
 * in-the-field pages (/present, /field, /punch, every public token page) are deliberately left out.
 *
 * useGateWake: the gates re-check on window focus, but a tab left open overnight often never gets a
 * focus event. This nudges them (a synthetic window 'focus') when the person comes back after 30
 * idle minutes, when the page becomes visible again, and when the local day changes.
 */
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth, useUser } from '@clerk/clerk-react'
import { ctx } from '../outline/ctx.js'
import { resolveCurrentMember } from '../services/members.js'
import { loadCurrentUserSeat } from '../services/tenancy.js'
import TimeGate from './TimeGate.jsx'
import CaptainReportGate from './CaptainReportGate.jsx'
import TriageGate from './TriageGate.jsx'
import ProgressGate from './ProgressGate.jsx'
import JourneyEntryModal from './JourneyEntryModal.jsx'
// The lockouts' own buttons open these (Open Time Card / Open Time Review); without them mounted here,
// a tool window like AlderCAD showed the lockout but its button opened nothing (Matt, 2026-10-19).
import TimeCardModal from './TimeCardModal.jsx'
import TimeReviewModal from './TimeReviewModal.jsx'
import ExpenseReviewModal from './ExpenseReviewModal.jsx'
import { workPassMinutes } from '../outline/lockoutPass.js'

// Standalone staff tools that open in their own window. The outline ('/', '/project') has its own.
// (Each mounts its own DialogHost, which the gates' confirm dialogs need; /admin does not, so it is out.)
const TOOL_PATHS = ['/modeler', '/qc', '/docqc', '/spec', '/sketch']

const IDLE_MS = 30 * 60 * 1000
function localDay() { const d = new Date(); return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate() }

export function useGateWake() {
  useEffect(() => {
    let lastInput = Date.now()
    let day = localDay()
    const wake = () => window.dispatchEvent(new Event('focus'))
    const onInput = () => {
      const now = Date.now()
      if (now - lastInput > IDLE_MS) wake()
      lastInput = now
    }
    const onVisible = () => { if (document.visibilityState === 'visible') wake() }
    const tick = setInterval(() => { const d = localDay(); if (d !== day) { day = d; wake() } }, 5 * 60 * 1000)
    const opts = { capture: true, passive: true }
    window.addEventListener('pointerdown', onInput, opts)
    window.addEventListener('keydown', onInput, opts)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(tick)
      window.removeEventListener('pointerdown', onInput, opts)
      window.removeEventListener('keydown', onInput, opts)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}

export default function LockoutHost() {
  const { pathname } = useLocation()
  const on = TOOL_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
  return on ? <ToolLockouts /> : null
}

function ToolLockouts() {
  const { getToken } = useAuth()
  const { user } = useUser()
  const [ready, setReady] = useState(false)
  // THE WORK PASS (outline/lockoutPass.js): opened from a lockout card to do the work it asks for, so
  // this tool window waits. Re-read every minute and on focus; when it runs out, the lockouts return.
  const [pass, setPass] = useState(() => workPassMinutes())
  const [passHidden, setPassHidden] = useState(false)
  useEffect(() => {
    const re = () => setPass(workPassMinutes())
    const t = setInterval(re, 60 * 1000)
    window.addEventListener('focus', re)
    return () => { clearInterval(t); window.removeEventListener('focus', re) }
  }, [])
  useGateWake()

  useEffect(() => { if (!window.__clerkGetToken) window.__clerkGetToken = () => getToken() }, [getToken])
  useEffect(() => {
    if (!user) return
    let cancelled = false
    ;(async () => {
      const email = user.primaryEmailAddress && user.primaryEmailAddress.emailAddress
      let member = null
      try { member = email ? await resolveCurrentMember(email) : null } catch { member = null }
      try { await loadCurrentUserSeat(user.id) } catch { /* gates fail open without a seat */ }
      if (cancelled || !member) return
      // Keep whatever display name the tool already set (AlderCAD shows it to collaborators).
      const prevName = ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields.Name
      ctx.currentMember = { ...member, fields: { ...(member.fields || {}), Name: (member.fields && member.fields.Name) || prevName || user.fullName || email } }
      setReady(true)
    })()
    return () => { cancelled = true }
  }, [user])

  if (!ready) return null
  if (pass > 0) {
    if (passHidden) return null
    return (
      <div role="status" style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 999990, display: 'flex', alignItems: 'center', gap: 10, maxWidth: 360, padding: '9px 12px', borderRadius: 8, background: '#07272D', color: '#fff', fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.4, boxShadow: '0 8px 24px rgba(4,21,24,0.3)' }}>
        <span>Opened from Triage, so it waits here for {pass >= 60 ? Math.round(pass / 6) / 10 + ' h' : pass + ' min'}. Answer the ask in your main window when you are done.</span>
        <button type="button" onClick={() => setPassHidden(true)} style={{ font: 'inherit', background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }} aria-label="Hide">×</button>
      </div>
    )
  }
  return (
    <>
      <TimeGate />
      <CaptainReportGate />
      <TriageGate />
      <ProgressGate />
      {/* the triage card's "Add to Client Hub…" opens this */}
      <JourneyEntryModal />
      <TimeCardModal />
      <TimeReviewModal />
      <ExpenseReviewModal />
    </>
  )
}
